// Helpers to manage document permissions in create and update components

export function getInitialGroupIds(document) {
  return (document?.userGroupAccess ?? [])
    .filter((g) => g?.name)
    .map((g) => g.id);
}

export const isUserInSelectedGroups = (user, selectedGroupIds = []) => {
  if (!selectedGroupIds.length) return false;

  const userGroupIds = user.groupAssignments?.map((assignment) => assignment.groupId) || [];

  return selectedGroupIds.some((selectedId) => userGroupIds.includes(selectedId));
};

export const getVisibleUsers = (users, selectedUserIds = [], fadingUserIds = []) => {
  if (!Array.isArray(users)) return [];

  return users.filter((user) => {
    const isSelected = selectedUserIds.includes(user.id);
    const isFading = fadingUserIds.includes(user.id);
    return isSelected || isFading;
  });
};

export const getSelectedUserIds = (selectedUsers) => {
  if (!Array.isArray(selectedUsers)) return [];
  return selectedUsers.map((selected) => (typeof selected === 'object' ? selected.id : selected));
};

export const handleUserChange = (options, setFieldValue, storeIds = false) => {
  if (storeIds) {
    const userIds = options ? options.map((user) => user.id) : [];
    setFieldValue('users', userIds);
    return;
  }

  setFieldValue('users', options || []);
};

// Check admin status of user to determine permission granting capabilities
const isPrimaryAdmin = (user) => Boolean(user?.primaryAdmin);

export const canGrantPermissionsToUser = (currentUser, candidateUser) => {
  if (!isPrimaryAdmin(currentUser) && isPrimaryAdmin(candidateUser)) return false;
  return true;
};

export const foldersRefiner = ({
  visibleFolders,
  userList,
  selectedGroupIds = [],
  selectedUserIds = [],
} = {}) => {
  if (!visibleFolders) return [];

  if (!selectedGroupIds.length && !selectedUserIds.length) {
    return visibleFolders;
  }

  const returnFolders = [];

  visibleFolders.forEach((folder) => {
    if (folder.globalAccess) {
      returnFolders.push(folder);
      return;
    }

    const folderGroupIds = (folder.userGroupAccess || []).map((group) => group.id);
    const folderIndividualIds = (folder.individualAccess || [])
      .filter((user) => user.first !== null && user.last !== null)
      .map((user) => user.id);

    const hasGroupsForSelection = !selectedGroupIds.length
      || selectedGroupIds.every((groupId) => folderGroupIds.includes(groupId));

    if (!hasGroupsForSelection) {
      return;
    }

    if (!selectedUserIds.length) {
      returnFolders.push(folder);
      return;
    }

    const allowedUserIds = new Set(folderIndividualIds);

    if (folderGroupIds.length && userList) {
      userList.forEach((user) => {
        const userGroupIds = user.groupAssignments?.map(
          (assignment) => assignment.groupId,
        ) || [];

        if (userGroupIds.some((groupId) => folderGroupIds.includes(groupId))) {
          allowedUserIds.add(user.id);
        }
      });
    }

    const allSelectedUsersAllowed = selectedUserIds.every(
      (userId) => allowedUserIds.has(userId),
    );

    if (allSelectedUsersAllowed) {
      returnFolders.push(folder);
    }
  });

  return returnFolders;
};

export const getFolderContext = ({ currentFolder, selectedFolder }) => {
  if (currentFolder) {
    return currentFolder;
  }

  if (selectedFolder && selectedFolder.id && selectedFolder.id !== 0) {
    return selectedFolder;
  }

  return null;
};

export const groupsRefiner = ({ userGroups, currentFolder, selectedFolder } = {}) => {
  if (!userGroups) return [];

  const folderContext = getFolderContext({ currentFolder, selectedFolder });

  if (!folderContext) {
    return userGroups;
  }

  const folderGroupAccess = folderContext.userGroupAccess || [];
  const folderIndividualAccess = folderContext.individualAccess || [];

  // Folder with only individual access should show no groups
  if (!folderGroupAccess.length && folderIndividualAccess.length) {
    return [];
  }

  // Folder with no access restrictions should show all user groups
  if (!folderGroupAccess.length) {
    return userGroups;
  }

  const userGroupIdsArray = userGroups.map((group) => group.id);

  // Only keep folder groups that exist in the userGroups list
  return folderGroupAccess.filter((group) => userGroupIdsArray.includes(group.id));
};

// Used in update modal to pre-populate folder select
export const defaultFolderFunction = (folderId, foldersForSelection) => {
  if (folderId === undefined || folderId === null) {
    return null;
  }

  return foldersForSelection.find((folder) => folder.id === folderId) || null;
};

// Used in update modal to prevent selecting users without access to folder
export const filterUsersByFolderGroups = (folder, users) => {
  if (!folder || !folder.userGroupAccess?.length) {
    return users;
  }

  const folderGroupIds = folder.userGroupAccess.map((group) => group.id);

  return users.filter((user) => {
    const userGroupIds = user.groupAssignments?.map((assignment) => assignment.groupId) || [];
    return folderGroupIds.some((folderGroupId) => userGroupIds.includes(folderGroupId));
  });
};

export const canEditGroupAccess = (document, userGroups) => {
  if (!document || !userGroups) return false;
  if (!userGroups.length) return false;

  const documentGroupIds = document.userGroupAccess
    .filter((group) => group.name !== null)
    .map((group) => group.id);

  const accessibleGroupIds = userGroups.map((group) => group.id);

  // If document has no group restrictions then we can edit
  if (documentGroupIds.length === 0) return true;

  // Can only edit if user has access to all document groups
  return documentGroupIds.every((id) => accessibleGroupIds.includes(id));
};

export const handleGroupChange = ({
  options,
  values,
  setFieldValue,
  userList,
  isUserInSelectedGroups: isUserInSelectedGroupsFunc,
  setFadingUsers,
  hasShownAutoRemovalToast,
  setHasShownAutoRemovalToast,
  showInfoToast: showInfoToastFunc,
  selectedGroupIds,
  setSelectedGroups,
  storeGroupsAsObjects,
}) => {
  const groupIds = options ? options.map((group) => group.id) : [];

  const previousGroupIds = Array.isArray(selectedGroupIds)
    ? selectedGroupIds
    : (values.groups || []).map((group) => (
      typeof group === 'object' ? group.id : group
    ));

  // Which users should be faded because they belong to a selected group
  const usersToRemove = userList?.filter((user) => {
    const selectedUsers = values.users || [];
    const userId = user.id;

    const userWasSelected = selectedUsers.some(
      (selected) => (typeof selected === 'object' ? selected.id : selected) === userId,
    );
    const userWillBeInGroup = isUserInSelectedGroupsFunc(user, groupIds);
    const userWasInGroup = isUserInSelectedGroupsFunc(user, previousGroupIds);

    return userWasSelected && userWillBeInGroup && !userWasInGroup;
  }).map((user) => user.id) || [];

  if (setSelectedGroups) {
    setSelectedGroups(groupIds);
  }

  // How we store groups in Formik depends on the modal
  if (storeGroupsAsObjects) {
    setFieldValue('groups', options || []);
  } else {
    setFieldValue('groups', groupIds);
  }

  // Fading animation with toast
  if (usersToRemove.length > 0) {
    setFadingUsers(usersToRemove);

    setTimeout(() => {
      const remainingUsers = (values.users || []).filter((selected) => {
        const userId = typeof selected === 'object' ? selected.id : selected;
        return !usersToRemove.includes(userId);
      }) || [];

      setFieldValue('users', remainingUsers);
      setFadingUsers([]);

      if (!hasShownAutoRemovalToast) {
        showInfoToastFunc(
          'Individuals were removed because they belong to one of the selected groups.',
        );
        setHasShownAutoRemovalToast(true);
      }
    }, 1500);
  }
};

export const usersRefiner = ({
  userList,
  currentFolder,
  selectedFolder,
  allowMainDirectoryBypass = false,
  currentUser,
}) => {
  if (!Array.isArray(userList)) return [];

  // In update the user can choose Main Directory and ignore any folder restrictions
  let filteredUserList = userList;
  if (currentUser) {
    filteredUserList = userList.filter((user) => canGrantPermissionsToUser(currentUser, user));
  }

  // If current user is a secondary admin then hide all admins from the list
  if (currentUser?.admin && !currentUser?.primaryAdmin) {
    filteredUserList = filteredUserList.filter(
      (user) => !user.admin,
    );
  }

  if (allowMainDirectoryBypass && selectedFolder && selectedFolder.id === 0) {
    return filteredUserList;
  }

  const folderContext = getFolderContext({ currentFolder, selectedFolder });

  if (!folderContext) {
    return filteredUserList;
  }

  const folderGroupAccess = folderContext.userGroupAccess || [];
  const folderIndividualAccess = folderContext.individualAccess || [];

  const hasGroupRestrictions = folderGroupAccess.length > 0;

  // Start from group based visibility rules if they exist
  let allowedUsers = filteredUserList;
  if (hasGroupRestrictions) {
    allowedUsers = filterUsersByFolderGroups(folderContext, filteredUserList);
  }

  const folderIndividualIds = folderIndividualAccess
    .filter((user) => user.first !== null && user.last !== null)
    .map((user) => user.id);

  // If there are only individual restrictions then limit list to those users
  if (!hasGroupRestrictions && folderIndividualIds.length) {
    return filteredUserList.filter((user) => folderIndividualIds.includes(user.id));
  }

  // If there are no individual restrictions then just return the group result
  if (!folderIndividualIds.length) {
    return allowedUsers;
  }

  // Otherwise we include any individuals specifically assigned to folder
  const extraUsers = filteredUserList.filter((user) => folderIndividualIds.includes(user.id));
  const mergedUsers = [...allowedUsers];

  extraUsers.forEach((user) => {
    if (!mergedUsers.some((existing) => existing.id === user.id)) {
      mergedUsers.push(user);
    }
  });

  return mergedUsers;
};

// Normalise selection into array of IDs
export const getSelectionIds = (selection) => (
  Array.isArray(selection)
    ? selection.map((item) => (typeof item === 'object' ? item.id : item))
    : []
);

// Get added and removed IDs between two arrays
export const getIdChanges = (originalIds, nextIds) => ({
  addedIds: nextIds.filter((id) => !originalIds.includes(id)),
  removedIds: originalIds.filter((id) => !nextIds.includes(id)),
});
